import type { GradientConfig } from '@ps/ds/icons/constants/gradients'

/**
 * Renders a `<linearGradient>` element with the given id.
 * Place it inside a `<defs>` in the same SVG that references it.
 */
export function renderLinearGradient(id: string, gradientConfig: GradientConfig): JSX.Element {
  return (
    <linearGradient
      id={id}
      x1={gradientConfig.x1 ?? '0%'}
      y1={gradientConfig.y1 ?? '0%'}
      x2={gradientConfig.x2 ?? '100%'}
      y2={gradientConfig.y2 ?? '100%'}
      gradientUnits={gradientConfig.gradientUnits ?? 'objectBoundingBox'}>
      {gradientConfig.stops.map((stop, stopIndex) => (
        <stop key={stopIndex} stopColor={stop.color} offset={stop.offset} />
      ))}
    </linearGradient>
  )
}

/**
 * Renders an invisible SVG containing gradient definitions for icon use.
 */
export function renderGradientDef(
  iconName: string,
  gradientConfig: GradientConfig | undefined,
): JSX.Element | null {
  if (!gradientConfig) return null

  return (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>{renderLinearGradient(`${iconName}-${gradientConfig.id}`, gradientConfig)}</defs>
    </svg>
  )
}

/**
 * Resolves a gradient identifier to its configuration.
 */
export function resolveGradient(
  gradient: GradientConfig | string | undefined,
  gradients: Record<string, GradientConfig>,
): GradientConfig | undefined {
  if (!gradient) return undefined
  return typeof gradient === 'string' ? gradients[gradient] : gradient
}

/**
 * Generates a gradient URL reference for SVG fill attributes.
 */
export function getGradientUrl(iconName: string, gradientId: string): string {
  return `url(#${iconName}-${gradientId})`
}
